import React, { forwardRef, useImperativeHandle, useState, useMemo } from "react";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import DataGrid from "devextreme-react/data-grid";
import {
  GetTransactionDetailGridData, GetItemMasterGridData, GetItemLotGridData, GetLocationMasterGridData,
} from "../services/ItemManagementService";
import { ITEM_COLUMNS, LOT_COLUMNS, LOCATION_COLUMNS } from "./referenceColumns";
import { ITEM_STATUSES } from "./codeLists";

const RANGE5 = [1, 2, 3, 4, 5];
const money = { type: "fixedPoint", precision: 2 };

// Shared DevExtreme grid options for every bottom sub-tab. A fixed height (not "100%") is required here:
// inside a TabPanel the grid doesn't inherit a definite height, so "100%" collapses the data area (pager
// with no rows). A concrete height gives room for rows + the pager.
const gridProps = {
  remoteOperations: true,
  showBorders: true,
  columnAutoWidth: true,
  height: 305,
  sorting: { mode: "multiple" },
  filterRow: { visible: true },
  headerFilter: { visible: true },
  columnChooser: { enabled: true },
  export: { enabled: true },
  paging: { pageSize: 20 },
  pager: { visible: true, showPageSizeSelector: true, allowedPageSizes: [20, 50, 100], showInfo: true },
};

// Exactly the Delphi grid column sets (uIMD0046.dfm TsxDBGridColumn collections). ROWID is the row key,
// never a column; the extra query fields the datasets carry are not defined as grid columns in the legacy.

// grdBottomDetail (frmIMM0047). Delphi-visible columns shown; the rest of the detail query's fields
// (record #, second descriptions, dates, cost, audit, and the detail UDF 1-5) are added hidden.
const DETAIL_COLUMNS = [
  { dataField: "rowid", visible: false, showInColumnChooser: false },
  { dataField: "itemCode", caption: "Item" },
  { dataField: "itemDescription", caption: "Item Description - 1" },
  { dataField: "lotCode", caption: "Lot" },
  { dataField: "lotDescription", caption: "Lot Description - 1" },
  { dataField: "status", caption: "Status", lookup: { dataSource: ITEM_STATUSES, valueExpr: "code", displayExpr: "label" } },
  { dataField: "classCode", caption: "Class" },
  { dataField: "subClassCode", caption: "Subclass" },
  { dataField: "amount", caption: "Amount", dataType: "number", format: money },
  { dataField: "recordNumber", caption: "Record #", visible: false },
  { dataField: "itemDescription2", caption: "Item Description - 2", visible: false },
  { dataField: "lotDescription2", caption: "Lot Description - 2", visible: false },
  { dataField: "quantity", caption: "Quantity", dataType: "number", format: money, visible: false },
  { dataField: "unitCost", caption: "Unit Cost", dataType: "number", format: money, visible: false },
  { dataField: "reference", caption: "Reference", visible: false },
  { dataField: "dateIntroduced", caption: "Date Introduced", dataType: "date", format: "MM/dd/yyyy", visible: false },
  { dataField: "dateAvailable", caption: "Date Available", dataType: "date", format: "MM/dd/yyyy", visible: false },
  { dataField: "dateExpiration", caption: "Date Expiration", dataType: "date", format: "MM/dd/yyyy", visible: false },
  { dataField: "storageBillingStartDate", caption: "Storage Billing Start", dataType: "date", format: "MM/dd/yyyy", visible: false },
  { dataField: "storageBillingEndDate", caption: "Storage Billing End", dataType: "date", format: "MM/dd/yyyy", visible: false },
  { dataField: "creationDateTime", caption: "Creation Date", dataType: "datetime", format: "MM/dd/yyyy hh:mm a", visible: false },
  { dataField: "creationUserId", caption: "Creation User", visible: false },
  { dataField: "lastUpdateDateTime", caption: "Last Update Date", dataType: "datetime", format: "MM/dd/yyyy hh:mm a", visible: false },
  { dataField: "lastUpdateUserId", caption: "Last Update User", visible: false },
  ...RANGE5.map((i) => ({ dataField: `code${i}`, caption: `Code - ${i}`, visible: false })),
  ...RANGE5.map((i) => ({ dataField: `reference${i}`, caption: `Reference - ${i}`, visible: false })),
  ...RANGE5.map((i) => ({ dataField: `date${i}`, caption: `Date - ${i}`, dataType: "date", format: "MM/dd/yyyy", visible: false })),
  ...RANGE5.map((i) => ({ dataField: `quantity${i}`, caption: `Quantity - ${i}`, dataType: "number", format: money, visible: false })),
];

// Item Master / Item/Lot / Location Master column sets live in referenceColumns.js (full Delphi field sets).

// Bottom sub-tab bar (Delphi IMD0046: Detail | Item Master | Item/Lot | Location Master). Only "Detail" is
// a master-detail child of the selected top transaction (re-fetched on selection, via the imperative
// `updateData` handle); the other three are standalone full-master reference lists.
const DetailTabs = forwardRef(({ accessToken }, ref) => {
  const [selectedRow, setSelectedRow] = useState(null);

  useImperativeHandle(ref, () => ({
    updateData(rowData) { setSelectedRow(rowData); },
  }));

  const detailStore = useMemo(
    () => (selectedRow && selectedRow.rowid ? GetTransactionDetailGridData(accessToken, selectedRow.rowid) : null),
    [selectedRow, accessToken]
  );
  const itemStore = useMemo(() => GetItemMasterGridData(accessToken), [accessToken]);
  const lotStore = useMemo(() => GetItemLotGridData(accessToken), [accessToken]);
  const locationStore = useMemo(() => GetLocationMasterGridData(accessToken), [accessToken]);

  return (
    <TabPanel deferRendering={true} animationEnabled={false}>
      <TabItem title="Detail">
        <DataGrid
          {...gridProps}
          dataSource={detailStore}
          columns={DETAIL_COLUMNS}
          noDataText={selectedRow ? "No detail lines" : "Select a transaction to view its detail"}
        />
      </TabItem>
      <TabItem title="Item Master">
        <DataGrid {...gridProps} dataSource={itemStore} columns={ITEM_COLUMNS} />
      </TabItem>
      <TabItem title="Item/Lot">
        <DataGrid {...gridProps} dataSource={lotStore} columns={LOT_COLUMNS} />
      </TabItem>
      <TabItem title="Location Master">
        <DataGrid {...gridProps} dataSource={locationStore} columns={LOCATION_COLUMNS} />
      </TabItem>
    </TabPanel>
  );
});

export default DetailTabs;
